import { create } from "zustand";
import { ChatHistory, ChatMessage, TokenUsage } from "@webuild-ai/admin/models/components";
import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import { WorkflowConfig } from "@webuild-ai/agent-platform/models/components";

import { logSSEPerformanceMetrics } from "@/utils/ssePerformance";
import { InvokeMessage } from "@/utils/chat/workflowExecution";
import {
  SSEEventType,
  isCompletionEvent,
  extractMessageId,
  extractMessageType,
  isStreamingEvent,
  isMessageCompletedEvent,
  MessageType,
} from "@/types/sse";
import { detectArtefacts } from "@/utils/ArtefactDetector";
import { logger } from "@/telemetry";

import { useArtefactStore } from "./ArtefactStore";
import { useChatHistoryStore } from "./ChatHistoryStore";

// Constants to maintain referential equality for empty arrays
const EMPTY_TOOL_EXECUTIONS: never[] = [];
const EMPTY_TOOL_CALLS: never[] = [];

/**
 * Extended ChatMessage type that includes messageType for differentiating
 * between user, assistant, and system messages in the UI.
 */
export interface ExtendedChatMessage extends ChatMessage {
  messageType?: MessageType;
}

export interface UploadedFile {
  name: string;
  size: string;
  type: string;
  id: string;
  status?: string; // For tracking upload progress: "uploading", "processing", etc.
}

export interface InputRequest {
  messageId: string;
  question: string;
  agent: string;
  timestamp: string;
}

export interface ChatStoreState {
  streaming: boolean;
  chatLoading: boolean;
  chatWorkflowId: string | null;
  runId: string | null;
  chat: ChatHistory | null;
  tokenUsage: TokenUsage | null;
  pendingInputRequest: InputRequest | null;

  create: (
    platformClient: WebuildAiPlatform,
    workflowConfig: WorkflowConfig,
    username: string,
    tenant: string,
    summary: string,
  ) => Promise<string>;
  invoke: (
    adminClient: WeBuildAiAdmin,
    platformClient: WebuildAiPlatform,
    data: InvokeMessage,
  ) => Promise<void>;
  subscribe: (
    sseWorkflowClient: (workflowId: string, lastEventId?: string) => EventSource | null,
    adminClient: WeBuildAiAdmin,
    userId: string,
    fetchChatHistory?: (userId: string, adminClient: WeBuildAiAdmin) => Promise<void>,
  ) => void;
  load: (adminClient: WeBuildAiAdmin, runId: string) => Promise<void>;
  submitFeedback: (
    adminClient: WeBuildAiAdmin,
    runId: string,
    messageId: string,
    liked: boolean,
    feedback: string,
  ) => Promise<void>;
  updateMessageFeedback: (messageId: string, liked: boolean | null) => void;
  updateMessageComment: (messageId: string, feedback: string) => void;
  updateMessage: (chatId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  stopStreaming: (
    adminClient: WeBuildAiAdmin,
    platformClient?: WebuildAiPlatform,
    discardMessages?: boolean,
  ) => Promise<void>;
  save: (adminClient: WeBuildAiAdmin, chatHistory: ChatHistory) => Promise<void>;
  newChat: () => void;
  setCurrentWorkflowId: (workflowId: string) => void;
  setOnChatSaved: (callback: ((chatId: string, workflowId: string) => void) | undefined) => void;
}

const useChatStore = create<
  ChatStoreState & {
    resetPrompt: boolean;
    triggerResetPrompt: () => void;
    clearResetPrompt: () => void;
    onChatSaved: ((chatId: string, workflowId: string) => void) | undefined;
  }
>((set, get) => {
  // Note: Zustand's set() should NEVER throw errors - it's synchronous and safe.
  // If we're seeing lifecycle errors, they're coming from React components,
  // not from the store itself. We should NOT trap them here.
  // Using direct set() and letting any real errors propagate.

  return {
    streaming: false,
    chatLoading: false,
    chatWorkflowId: null,
    runId: null,
    chat: null,
    tokenUsage: null,
    pendingInputRequest: null,

    resetPrompt: false,
    triggerResetPrompt: () => set({ resetPrompt: true }),
    clearResetPrompt: () => set({ resetPrompt: false }),

    // Chat saved callback for URL state synchronization
    onChatSaved: undefined as ((chatId: string, workflowId: string) => void) | undefined,

    newChat: () => {
      logger.info("🆕 ChatStore.newChat: Starting new chat, clearing selection");

      // Clear the chat history selection FIRST before anything else
      useChatHistoryStore.getState().clearSelection();

      // Clean up any active SSE connections before starting new chat
      const currentRunId = get().runId;

      if (currentRunId) {
        const activeConnections = (window as any).__activeSSEConnections || {};
        const connection = activeConnections[currentRunId];

        if (connection) {
          logger.info("🛑 Cleaning up SSE connection for new chat", {
            runId: String(currentRunId),
          });

          // Abort the connection using AbortController
          if (connection.abortController) {
            connection.abortController.abort();
          }

          // Call cleanup function
          if (typeof connection.cleanup === "function") {
            connection.cleanup();
          }

          // Remove from registry
          delete activeConnections[currentRunId];
        }
      }

      // Clear any workflow-specific localStorage that might persist state
      try {
        const keysToRemove = [];

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);

          if (key && key.startsWith("chat_")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));

        if (keysToRemove.length > 0) {
          logger.info("🧹 ChatStore.newChat: Cleared chat-related localStorage keys", {
            keys: String(keysToRemove),
          });
        }
      } catch (error) {
        logger.warn("Failed to clear chat-related localStorage in newChat", {
          error: String(error),
        });
      }

      set({
        runId: null,
        chat: null,
        chatWorkflowId: null,
        streaming: false,
        pendingInputRequest: null,
      });

      // Clear artefacts and citations from ArtefactStore
      useArtefactStore.getState().clearArtefacts();
      useArtefactStore.getState().clearCitations();
    },

    setCurrentWorkflowId: (workflowId: string) => {
      set({ chatWorkflowId: workflowId });
    },

    setOnChatSaved: (callback: ((chatId: string, workflowId: string) => void) | undefined) => {
      set({ onChatSaved: callback });
    },

    save: async (adminClient: WeBuildAiAdmin, chatHistory: ChatHistory) => {
      if (!adminClient || !chatHistory) return;

      try {
        logger.info("💾 Saving chat...");

        const currentChat = get().chat;

        // Only update state if the chat has actually changed
        if (currentChat?.id === chatHistory.id) {
          // Check if there are meaningful differences before updating state
          const currentMessagesLength = currentChat.messages?.length || 0;
          const newMessagesLength = chatHistory.messages?.length || 0;
          const lastCurrentMessage =
            currentMessagesLength > 0 ? currentChat.messages[currentMessagesLength - 1] : null;
          const lastNewMessage =
            newMessagesLength > 0 ? chatHistory.messages[newMessagesLength - 1] : null;

          // Only update if message count changed or last message content/status changed
          const shouldUpdate =
            currentMessagesLength !== newMessagesLength ||
            lastCurrentMessage?.content !== lastNewMessage?.content ||
            lastCurrentMessage?.status !== lastNewMessage?.status;

          if (shouldUpdate) {
            set({ chat: chatHistory });
          }
        } else if (!currentChat) {
          // Set initial chat if none exists
          set({ chat: chatHistory });
        }

        // Store in localStorage as backup
        const chatStorageKey = `chat_${chatHistory.id}`;
        const updatedChat = get().chat;

        if (updatedChat?.id === chatHistory.id) {
          localStorage.setItem(chatStorageKey, JSON.stringify(updatedChat));
          logger.info(`💾 Chat ${chatHistory.id} backed up to localStorage`);
        }

        logger.info("✅ Chat save completed");

        // Trigger URL state callback if registered
        const { onChatSaved, chatWorkflowId } = get();

        if (onChatSaved && chatHistory.id && chatWorkflowId) {
          logger.info("🔗 Triggering URL update callback", {
            chatId: String(chatHistory.id),
            workflowId: String(chatWorkflowId),
          });
          onChatSaved(chatHistory.id, chatWorkflowId);
        }
      } catch (error) {
        logger.error("❌ Error in save method", error);
      }
    },

    stopStreaming: async (
      adminClient: WeBuildAiAdmin,
      platformClient?: WebuildAiPlatform,
      discardMessages: boolean = true,
    ) => {
      const currentChat = get().chat;

      if (!currentChat) return;

      const runId = get().runId;

      // First, call the backend stop endpoint to cancel server-side execution
      // This ensures the workflow stops processing and messages are properly handled
      if (runId && platformClient) {
        try {
          // Use the SDK method for stopping workflows
          await platformClient.workflows.stopStreaming({
            workflowId: String(runId),
            discardMessages: discardMessages,
          });

          // Mark the message as stopped but keep it visible
          // Messages will be removed when user sends a new message (handled in invoke)
        } catch (error) {
          logger.error("Error calling backend stop endpoint", { error: String(error) });
          // Continue with client-side cleanup even if backend call fails
        }
      }

      // Update local state to reflect stopped status - keep message visible
      // The stopped message will be removed when user sends a new message (handled in invoke)
      const lastMessage = currentChat.messages
        .slice()
        .reverse()
        .find((msg: ChatMessage) => msg.role === "assistant" && msg.status === "streaming");

      if (lastMessage) {
        const updatedMessages = currentChat.messages.map((msg: ChatMessage) =>
          msg.id === lastMessage.id ? { ...msg, status: "stopped" as const } : msg,
        );
        const updatedChat = { ...currentChat, messages: updatedMessages };

        set({ chat: updatedChat });
        await get().save(adminClient, updatedChat);
      }

      set({ streaming: false });

      // Close the SSE connection if it exists
      if (runId && (window as any).__activeSSEConnections?.[runId]) {
        logger.info("🛑 Stopping SSE connection for runId", { runId: String(runId) });
        (window as any).__activeSSEConnections[runId].cleanup();
      }
    },

    create: async (platformClient, workflowConfig, username, tenant, summary) => {
      logger.info("🚀 ChatStore.create called", {
        hasPlatformClient: String(!!platformClient),
        hasWorkflowConfig: String(!!workflowConfig),
        workflowId: String(workflowConfig?.id),
        workflowName: String(workflowConfig?.name),
        username,
        tenant,
        summary,
      });

      if (!platformClient || !workflowConfig) {
        logger.error("❌ Missing required parameters", undefined, {
          platformClient: String(!!platformClient),
          workflowConfig: String(!!workflowConfig),
        });
        throw new Error("Platform client or workflow config not found");
      }

      // Validate workflow structure for SDK 0.14.0
      if (!workflowConfig.id || !workflowConfig.name) {
        logger.error("❌ Invalid workflow structure", undefined, {
          hasId: String(!!workflowConfig.id),
          hasName: String(!!workflowConfig.name),
          workflowConfig: JSON.stringify(workflowConfig),
        });
        throw new Error(
          `Invalid workflow structure - missing required fields: ${!workflowConfig.id ? "id " : ""}${!workflowConfig.name ? "name" : ""}`,
        );
      }

      if (get().streaming) {
        throw new Error("Streaming is already in progress");
      }

      let modelUsed = "";

      if (workflowConfig.agents) {
        const agentWithModel = Object.values(workflowConfig.agents).find(
          (agent) => agent.parameters?.model,
        );

        if (agentWithModel && agentWithModel.parameters?.model) {
          modelUsed = String(agentWithModel.parameters.model);
        }
      }

      logger.info("🌐 Calling platformClient.workflows.create", {
        workflowConfigKeys: String(Object.keys(workflowConfig || {})),
      });

      const response = await platformClient.workflows.create({
        createWorkflowReq: { workflow: workflowConfig },
      });

      logger.info("✅ Workflow creation successful", {
        workflowId: String(response.workflowId),
        hasResponse: String(!!response),
      });
      const chatObj: ChatHistory = {
        id: response.workflowId,
        tenant,
        username,
        timestamp: new Date().toISOString(),
        modelUsed,
        messages: [],
        metrics: {
          usage: {},
        },
        summary,
      };

      set({
        runId: response.workflowId,
        chat: chatObj,
        chatWorkflowId: workflowConfig.id,
      });

      // Add the new chat to the history sidebar immediately
      // Convert ChatHistory to ChatHistoryMetadata for the sidebar
      const chatMetadata = {
        id: chatObj.id,
        tenant: chatObj.tenant,
        username: chatObj.username,
        fileLocation: "", // Will be set by backend when persisted
        summary: chatObj.summary,
        createdAt: chatObj.timestamp,
        updatedAt: chatObj.timestamp,
        messageCount: 0,
        status: "streaming",
        workflowType: workflowConfig.id,
        metrics: chatObj.metrics,
      };

      useChatHistoryStore.getState().addNewChat(chatMetadata);

      logger.info("💭 Chat created in memory, waiting for platform to persist to backend...");

      return response.workflowId;
    },

    invoke: async (adminClient, platformClient, data) => {
      const { runId, chat, pendingInputRequest } = get();

      if (!runId || !chat || !platformClient || !adminClient) {
        throw new Error("Missing required dependencies for chat invocation");
      }
      if (get().streaming) {
        return;
      }
      set({ streaming: true });

      // Check if the last assistant message is "stopped" - if so, remove it and its user message
      // This ensures stopped messages disappear when user sends a new message
      let messagesToUse = chat.messages;
      const lastAssistantMsg = [...chat.messages]
        .reverse()
        .find((msg: ChatMessage) => msg.role === "assistant");

      if (lastAssistantMsg && lastAssistantMsg.status === "stopped") {
        // Find the user message that triggered the stopped response
        const lastAssistantIndex = chat.messages.findIndex(
          (msg: ChatMessage) => msg.id === lastAssistantMsg.id,
        );

        if (lastAssistantIndex > 0) {
          // Look for the user message just before the stopped assistant message
          let userMsgIndex = lastAssistantIndex - 1;

          while (userMsgIndex >= 0 && chat.messages[userMsgIndex].role !== "user") {
            userMsgIndex--;
          }

          if (userMsgIndex >= 0) {
            // Remove from the user message onwards (user msg + stopped assistant response)
            messagesToUse = chat.messages.slice(0, userMsgIndex);
          }
        }
      }

      const userMsg: ChatMessage = {
        id: String(messagesToUse.length + 1),
        role: "user",
        // Store full message data including mentions for display purposes
        // data may include extended mention fields not in the base ChatMessage content type
        content: data as unknown as ChatMessage["content"],
        status: "success",
        timestamp: new Date().toISOString(),
      };
      const updatedChat = { ...chat, messages: [...messagesToUse, userMsg] };

      set({ chat: updatedChat });
      await get().save(adminClient, updatedChat);

      // Check if this is a response to a human-in-the-loop input request
      if (pendingInputRequest) {
        logger.info("📤 Submitting user input for human-in-the-loop (via invoke endpoint)", {
          runId: String(runId),
          messageId: pendingInputRequest.messageId,
        });

        // Clear the pending input request BEFORE invoking
        // The backend will detect workflow.waitingUserInput and route correctly
        set({ pendingInputRequest: null });
      }

      // Always use the normal invoke endpoint
      // The backend detects if workflow is waiting for input and routes accordingly
      logger.info("🚀 Invoking workflow", {
        runId: String(runId),
        runIdType: typeof runId,
        isInputResponse: String(!!pendingInputRequest),
      });

      // SDK 0.11.0 original structure
      // Type assertion needed because SDK type doesn't include our extended mention fields

      await platformClient.workflows.invoke({
        workflowId: String(runId),
        // SDK type does not include extended mention fields — cast via unknown
        invokeWorkflowReq: {
          data: data as unknown as Record<
            string,
            string | string[] | Record<string, string> | Record<string, string>[]
          >,
        },
      });

      logger.info("✅ Workflow invoke successful");
    },

    subscribe: (sseWorkflowClient, adminClient, userId, fetchChatHistory) => {
      const { runId } = get();

      if (!runId || !sseWorkflowClient || !adminClient) {
        throw new Error("Missing required dependencies");
      }

      logger.info("🔌 Subscribe called for runId", { runId: String(runId) });

      // Initialize global SSE connection tracking if it doesn't exist
      if (!(window as any).__activeSSEConnections) {
        (window as any).__activeSSEConnections = {};
      }

      // Check if there's already an active SSE connection for this runId
      const existingConnection = (window as any).__activeSSEConnections[runId];

      if (existingConnection) {
        logger.info("🔌 SSE connection already active for runId", { runId: String(runId) });

        return existingConnection.cleanup;
      }

      // --- SSE Reconnection State (ADR-0014) ---
      // Mutable ref so event handlers always use the current EventSource instance
      let currentSe: EventSource | null = sseWorkflowClient(runId);

      if (!currentSe) return;

      // Track the last SSE event ID for reconnection replay (monotonically incrementing integer)
      let lastEventId: string | null = null;
      // Track when we last received any event (including heartbeats) for dead-connection detection
      let lastEventTimestamp = Date.now();
      // Heartbeat check interval — polls every 10s, triggers reconnect after 60s of silence
      let heartbeatCheckInterval: ReturnType<typeof setInterval> | null = null;
      // Track the highest processed event ID to skip duplicates on replay
      let lastProcessedEventId = 0;
      // Reconnect attempt counter for logging
      let reconnectCount = 0;
      // Flag to skip gap detection on first event after reconnect (heartbeat IDs
      // increment the backend counter, creating expected gaps that aren't data loss)
      let skipNextGapCheck = false;
      // Maximum reconnect attempts before giving up
      const MAX_RECONNECT_ATTEMPTS = 10;
      // Heartbeat timeout threshold (2 missed heartbeats at 30s interval)
      const HEARTBEAT_TIMEOUT_MS = 60_000;
      // Heartbeat check polling interval
      const HEARTBEAT_CHECK_INTERVAL_MS = 10_000;
      // Timeout for REST API fallback when recovering from replay buffer overflow
      const API_REFRESH_TIMEOUT_MS = 10_000;

      // Track if this SSE connection is still active
      let cleanupTimeoutId: ReturnType<typeof setTimeout> | null = null;

      // Create AbortController for proper cancellation - single source of truth
      const abortController = new AbortController();
      const { signal } = abortController;

      /**
       * Register all SSE event listeners on an EventSource instance.
       * Called on initial connection and after each reconnection.
       */
      const registerEventListeners = (se: EventSource) => {
        se.addEventListener(SSEEventType.MESSAGE, handleOutput);
        se.addEventListener(SSEEventType.OUTPUT, handleOutput);
        se.addEventListener(SSEEventType.REGENERATION, handleOutput);
        se.addEventListener(SSEEventType.REGENERATION_STARTED, handleRegenerationStarted);
        se.addEventListener(SSEEventType.WORKFLOW_COMPLETED, handleWorkflowCompletion);
        se.addEventListener(SSEEventType.MESSAGE_COMPLETED, handleMessageCompletion);
        se.addEventListener(SSEEventType.REGENERATION_COMPLETED, handleMessageCompletion);
        se.addEventListener(SSEEventType.ERROR, handleError);
        se.addEventListener(SSEEventType.INPUT, handleInputRequest);
        // Heartbeat listener — resets the dead-connection timer (ADR-0014)
        se.addEventListener(SSEEventType.HEARTBEAT, handleHeartbeat);
      };

      /**
       * Heartbeat handler — reset the liveness timer on each heartbeat.
       */
      const handleHeartbeat = (event: MessageEvent) => {
        if (signal.aborted) return;
        lastEventTimestamp = Date.now();
        // Track the event ID from heartbeats too
        if (event.lastEventId) {
          lastEventId = event.lastEventId;
        }
      };

      /**
       * Update liveness timestamp and event ID tracking on any received event.
       * Called at the top of every event handler.
       */
      const trackEvent = (event: MessageEvent) => {
        lastEventTimestamp = Date.now();
        if (event.lastEventId) {
          lastEventId = event.lastEventId;
        }
      };

      // Whether a REST API refresh is in progress (prevents duplicate fetches)
      let isRefreshingFromAPI = false;

      /**
       * Process an incoming event's ID for deduplication and gap detection.
       * Returns true if the event should be skipped (already processed).
       *
       * Side effects:
       * - Updates `lastProcessedEventId` to track highest seen ID
       * - Detects gaps in event IDs after reconnect (buffer overflow)
       *   and triggers REST API fallback to recover missing content
       *
       * Note: gap detection is skipped for the first event after reconnect
       * because heartbeat IDs increment the backend counter during disconnect,
       * creating expected gaps that don't represent data loss.
       */
      const processEventIdAndCheckDuplicate = (event: MessageEvent): boolean => {
        if (!event.lastEventId) return false;
        const eventId = parseInt(event.lastEventId, 10);

        if (isNaN(eventId)) return false;

        if (eventId <= lastProcessedEventId) {
          logger.info("🔁 Skipping duplicate event after reconnect", {
            eventId: String(eventId),
            lastProcessed: String(lastProcessedEventId),
          });

          return true;
        }

        // Detect gap: if the incoming event ID is more than 1 ahead of what we last processed,
        // events were lost from the replay buffer (buffer overflow during disconnect).
        // Skip gap check on first event after reconnect — heartbeat IDs increment the
        // backend counter during disconnect, so a gap is expected and doesn't mean data loss.
        const gap = eventId - lastProcessedEventId;

        if (gap > 1 && reconnectCount > 0 && !skipNextGapCheck && !isRefreshingFromAPI) {
          logger.warn("⚠️ Gap detected in SSE event IDs after reconnect — fetching full state", {
            lastProcessed: String(lastProcessedEventId),
            receivedId: String(eventId),
            missedEvents: String(gap - 1),
          });
          refreshChatFromAPI();
        }

        // Reset the skip flag after first event post-reconnect
        if (skipNextGapCheck) {
          skipNextGapCheck = false;
        }

        // Update the highest processed event ID
        lastProcessedEventId = eventId;

        return false;
      };

      /**
       * Fetch the full chat state from REST API to recover content lost
       * during a reconnection gap (when replay buffer overflowed).
       *
       * Note: The REST API only returns completed messages — the currently
       * streaming message is not persisted until message_completed. The user
       * may see a gap in the in-progress response, but message_completed
       * delivers the full final text which replaces the partial content.
       *
       * To avoid a visible flash where partial streaming content disappears,
       * the last in-progress message is preserved and appended after the
       * API messages.
       */
      const refreshChatFromAPI = async () => {
        if (isRefreshingFromAPI || signal.aborted) return;
        isRefreshingFromAPI = true;

        try {
          logger.info("🔄 Fetching full chat state from API after reconnect gap", {
            runId: String(runId),
          });

          const chatHistory = await Promise.race([
            adminClient.chatHistories.get({ id: runId }),
            new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error("Chat refresh timeout")), API_REFRESH_TIMEOUT_MS);
            }),
          ]);

          if (signal.aborted) return;

          if (chatHistory && chatHistory.messages) {
            const chat = get().chat;

            if (chat) {
              const apiMessages = chatHistory.messages as ChatMessage[];

              // Preserve the last in-progress streaming message to avoid a visible flash
              // where partial content disappears then reappears when message_completed arrives
              const lastMsg = chat.messages[chat.messages.length - 1];
              const isStreaming =
                lastMsg && lastMsg.role === "assistant" && lastMsg.status === "streaming";
              const merged = isStreaming ? [...apiMessages, lastMsg] : apiMessages;

              set({ chat: { ...chat, messages: merged } });
              logger.info("✅ Chat state recovered from API after reconnect gap", {
                messageCount: String(merged.length),
                preservedStreamingMsg: String(!!isStreaming),
              });
            }
          }
        } catch (error) {
          logger.warn("⚠️ Failed to refresh chat from API after reconnect gap", {
            error: String(error),
          });
          // Non-fatal: streaming continues, user just has a gap in content
        } finally {
          isRefreshingFromAPI = false;
        }
      };

      /**
       * Force reconnect after detecting a dead connection (ADR-0014).
       * Closes the current EventSource, creates a new one with Last-Event-ID,
       * and re-registers all event listeners.
       */
      const forceReconnect = () => {
        if (signal.aborted) return;

        reconnectCount++;

        if (reconnectCount > MAX_RECONNECT_ATTEMPTS) {
          logger.error("🔌 Max SSE reconnect attempts reached, giving up", {
            runId: String(runId),
            attempts: String(reconnectCount),
          });
          // Show error to user and stop streaming
          const chat = get().chat;

          if (chat && !signal.aborted) {
            const errorMsg: ChatMessage = {
              id: String(Date.now()),
              role: "assistant",
              content: "Connection lost after multiple reconnection attempts. Please try again.",
              status: "failed",
              liked: null,
              timestamp: new Date().toISOString(),
            };

            set({
              chat: { ...chat, messages: [...chat.messages, errorMsg] },
              streaming: false,
            });
          }
          cleanup();

          return;
        }

        logger.info("🔄 SSE reconnecting (dead connection detected)", {
          runId: String(runId),
          attempt: String(reconnectCount),
          lastEventId: lastEventId ?? "none",
          silentSeconds: String(Math.round((Date.now() - lastEventTimestamp) / 1000)),
        });

        // Close the hung EventSource
        if (currentSe) {
          currentSe.close();
        }

        // Create a new EventSource with Last-Event-ID for replay.
        // Backend confirmed: "0" triggers replay from beginning of history
        // (int("0") = 0, backend event IDs start at 1, so event.id > 0 matches all).
        // sseWorkflowClient is safe to capture in closures — getAccessTokenSilently()
        // is called per-request inside the fetch override, so reconnections always use a fresh token.
        const reconnectId = lastEventId ?? "0";
        const newSe = sseWorkflowClient(runId, reconnectId);

        if (!newSe) {
          logger.error("🔌 Failed to create new EventSource on reconnect", {
            runId: String(runId),
          });
          cleanup();

          return;
        }

        // Swap the current EventSource reference and re-register listeners
        currentSe = newSe;
        registerEventListeners(newSe);

        // Skip gap detection on the first event after reconnect — heartbeat IDs
        // increment the backend counter during disconnect, creating expected gaps
        skipNextGapCheck = true;

        // Reset liveness timestamp
        lastEventTimestamp = Date.now();

        logger.info("✅ SSE reconnected successfully", {
          runId: String(runId),
          attempt: String(reconnectCount),
          lastEventId: reconnectId,
        });
      };

      /**
       * Start the heartbeat check interval.
       * Polls every 10s and triggers reconnect if no event in 60s.
       */
      const startHeartbeatCheck = () => {
        if (heartbeatCheckInterval) {
          clearInterval(heartbeatCheckInterval);
        }
        heartbeatCheckInterval = setInterval(() => {
          if (signal.aborted) {
            if (heartbeatCheckInterval) {
              clearInterval(heartbeatCheckInterval);
              heartbeatCheckInterval = null;
            }

            return;
          }

          const silentMs = Date.now() - lastEventTimestamp;

          if (silentMs > HEARTBEAT_TIMEOUT_MS) {
            logger.warn("💀 SSE heartbeat timeout — no event received", {
              runId: String(runId),
              silentSeconds: String(Math.round(silentMs / 1000)),
            });
            forceReconnect();
          }
        }, HEARTBEAT_CHECK_INTERVAL_MS);
      };

      // Start monitoring for dead connections
      startHeartbeatCheck();

      const cleanup = () => {
        // Performance telemetry - start cleanup measurement
        const cleanupStart = performance.now();

        // Abort FIRST - this makes signal.aborted true atomically
        abortController.abort();

        if (currentSe) {
          currentSe.close();
        }
        // Clear heartbeat check interval (ADR-0014)
        if (heartbeatCheckInterval) {
          clearInterval(heartbeatCheckInterval);
          heartbeatCheckInterval = null;
        }
        // Clear any pending cleanup timeout
        if (cleanupTimeoutId) {
          clearTimeout(cleanupTimeoutId);
          cleanupTimeoutId = null;
        }
        // Remove from global tracking
        if ((window as any).__activeSSEConnections) {
          delete (window as any).__activeSSEConnections[runId];
        }
        logger.info("🔌 SSE connection cleaned up for runId", { runId: String(runId) });

        // Performance telemetry - end cleanup measurement
        performance.measure("sse-cleanup", { start: cleanupStart, end: performance.now() });

        // Log performance metrics in development mode
        if (process.env.NODE_ENV === "development") {
          // Delay slightly to ensure all measurements are captured
          setTimeout(() => logSSEPerformanceMetrics(), 100);
        }
      };

      // Register this connection globally with cleanup timeout cancellation and abort controller
      (window as any).__activeSSEConnections[runId] = {
        cleanup,
        isActive: () => !signal.aborted, // Use abort signal as single source of truth
        abortController, // Expose for external cancellation
        signal, // Expose signal for checking if aborted
        clearCleanupTimeout: () => {
          if (cleanupTimeoutId) {
            clearTimeout(cleanupTimeoutId);
            cleanupTimeoutId = null;
            logger.info("⏸️ Cancelled delayed cleanup for runId", { runId: String(runId) });
          }
        },
      };

      const handleOutput = (event: MessageEvent) => {
        // Performance telemetry - start measurement
        const messageProcessStart = performance.now();

        // Track liveness and event ID for reconnection (ADR-0014)
        trackEvent(event);

        // Check abort signal - single source of truth for cleanup state
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring output event");
          // Telemetry: Quick abort - no processing needed
          performance.measure("sse-message-abort", {
            start: messageProcessStart,
            end: performance.now(),
          });

          return;
        }

        // Skip duplicate events after reconnection replay (ADR-0014)
        if (processEventIdAndCheckDuplicate(event)) return;

        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (error) {
          logger.error("Failed to parse SSE message", error);

          return;
        }

        const data = msg.data || msg.content || "";
        const agent = msg.agent;
        const messageId = extractMessageId(msg);
        const messageType = extractMessageType(msg);
        const isRegenerationEvent = msg.is_regeneration || false;
        const chunkNumber = msg.chunk_number || null;
        const totalChunks = msg.total_chunks || null;

        if (!messageId) {
          throw new Error("Message ID is required");
        }

        // Validate streaming events have required data
        if (isStreamingEvent(msg) && !data) {
          logger.warn(`⚠️ Streaming event missing data`, { messageId, type: String(msg.type) });

          return;
        }

        // Enhanced logging for message flow analysis
        logger.info(`📨 SSE MESSAGE RECEIVED`, {
          messageId,
          chunkNumber: String(chunkNumber),
          totalChunks: String(totalChunks),
          isRegenerationEvent: String(isRegenerationEvent),
          dataLength: String(data?.length),
          dataPreview: data?.substring(0, 100) || "[empty]",
          eventType: String(msg.type || "content"),
        });

        // SPECIAL TRACKING for chunk 1 - this should help us see what happens to the first chunk
        if (chunkNumber === 1 || (data && data.length > 0 && !chunkNumber && isRegenerationEvent)) {
          logger.info(`🚨 FIRST CHUNK TRACKING`, {
            messageId,
            chunkNumber: String(chunkNumber || "unknown"),
            isRegenerationEvent: String(isRegenerationEvent),
            dataLength: String(data?.length),
            fullData: String(data),
          });
        }

        // Filter out system/debug messages that shouldn't be displayed to users
        const isSystemMessage =
          data &&
          (data.includes("Direct call completed") ||
            data.includes("response via agent reply mechanism") ||
            data.includes("agent reply mechanism") ||
            data.match(/^(System:|Debug:|Status:)/i));

        if (isSystemMessage) {
          logger.info(`🔧 Filtered out system message: ${data.substring(0, 100)}...`);

          return;
        }

        // Skip empty content messages (informational/keep-alive events)
        // BUT preserve completion events even if they have no content
        const isCompletionEventCheck = isCompletionEvent(msg);

        if (!data && !isCompletionEventCheck) {
          logger.info(`🔇 Skipping empty SSE message for ${messageId}`);

          return;
        }

        const chat = get().chat;

        if (!chat || signal.aborted) return;

        logger.info(`🔍 MESSAGE PROCESSING`, {
          messageId,
          chunkNumber: String(chunkNumber),
          chatId: String(chat.id),
          messageCount: String(chat.messages.length),
          isActive: String(!signal.aborted),
          isRegenerationEvent: String(isRegenerationEvent),
        });

        // Find existing message by ID
        const messageIndex = chat.messages.findIndex((m: ChatMessage) => m.id === messageId);

        logger.info(`🔎 MESSAGE LOOKUP`, {
          searchingForMessageId: messageId,
          foundAtIndex: String(messageIndex),
          messageCount: String(chat.messages.length),
          chunkNumber: String(chunkNumber),
        });

        if (messageIndex !== -1) {
          // Update existing message with streaming data
          const message = chat.messages[messageIndex];

          logger.info(`📝 Found message to update`, {
            messageId,
            currentStatus: String(message.status),
            attempts: String(message.attempts),
            contentLength: String(typeof message.content === "string" ? message.content.length : 0),
          });

          if (message.status === "stopped") {
            logger.info(`⏹️ Message ${messageId} is stopped, ignoring SSE event`);

            return;
          }

          // Check if this is a regeneration
          // We can detect regeneration if:
          // 1. Backend explicitly flags it as regeneration
          // 2. Message has attempts > 1
          const isRegeneration = isRegenerationEvent || (message.attempts && message.attempts > 1);

          // Debug logging for regeneration streaming
          if (isRegeneration) {
            const contentStr = typeof message.content === "string" ? message.content : "";

            logger.info(`🔄 REGENERATION STREAMING DETECTED`, {
              messageId,
              isRegenerationEvent: String(isRegenerationEvent),
              attempts: String(message.attempts),
              status: String(message.status),
              currentContentLength: String(contentStr.length),
              newDataLength: String(data?.length || 0),
            });
          }

          // Handle content for streaming
          const currentContent = typeof message.content === "string" ? message.content : "";
          let newContent;

          // For regeneration, check if we need to clear content first
          if (isRegeneration) {
            // First chunk detection: current content is empty
            const isFirstChunk = currentContent === "" || !currentContent;

            logger.info(`🔍 FIRST CHUNK DETECTION`, {
              messageId,
              contentIsEmpty: String(isFirstChunk),
              isRegenerationEvent: String(isRegenerationEvent),
              contentLength: String(currentContent?.length),
              dataLength: String(data?.length),
            });

            if (isFirstChunk) {
              // First chunk of regeneration - replace content entirely
              newContent = data;
              logger.info(`🔄 REGENERATION: First chunk detected, REPLACING content`, {
                chunkNumber: String(chunkNumber),
                oldContentLength: String(currentContent?.length),
                newContentLength: String(data?.length),
              });

              // CRITICAL: Special logging for chunk 1 replacement
              if (chunkNumber === 1) {
                logger.info(`🚨 CHUNK 1 REPLACEMENT HAPPENING`, {
                  messageId,
                  beforeContentLength: String(currentContent?.length || 0),
                  afterContentLength: String(data?.length || 0),
                  messageStatus: String(message.status),
                  attempts: String(message.attempts),
                });
              }
            } else {
              // Subsequent chunks - append
              newContent = currentContent + data;
              logger.info(`🔄 REGENERATION: Appending chunk`, {
                chunkNumber: String(chunkNumber),
                currentLength: String(currentContent?.length),
                appendingLength: String(data?.length),
                newTotalLength: String(newContent.length),
              });
            }
          } else {
            // Normal streaming - always append
            newContent = currentContent + data;
            logger.info(`📝 Normal streaming: Appending chunk, new length: ${newContent.length}`);
          }

          // Only update if content actually changed
          if (message.content !== newContent) {
            const updatedMessages = [...chat.messages];

            updatedMessages[messageIndex] = {
              ...message,
              content: newContent,
            };

            // CRITICAL: Log every content update with details
            logger.info(`📝 CONTENT UPDATE APPLIED`, {
              messageId,
              chunkNumber: String(chunkNumber),
              oldContentLength: String(
                typeof message.content === "string" ? message.content.length : 0,
              ),
              newContentLength: String(newContent.length),
              isFirstChunk: String(chunkNumber === 1),
            });

            // Only update state if SSE connection is still active
            if (!signal.aborted) {
              // Performance telemetry - measure state update time
              const stateUpdateStart = performance.now();

              // Let React batch updates naturally (React 18 automatic batching)
              set({ chat: { ...chat, messages: updatedMessages } });
              performance.measure("sse-state-update", {
                start: stateUpdateStart,
                end: performance.now(),
              });
            }
          }
        } else {
          // IMPORTANT: Only treat as regeneration if explicitly flagged by backend
          // Do NOT assume regeneration just because last message is assistant
          // In multi-agent workflows, each agent creates a NEW message
          const lastMessage = chat.messages[chat.messages.length - 1];
          let messageToReplace: ChatMessage | null = null;
          let isRegenerationContext = false;

          // Only consider regeneration if backend explicitly flags it
          if (isRegenerationEvent) {
            // If last message is assistant, replace it
            if (lastMessage && lastMessage.role === "assistant") {
              isRegenerationContext = true;
              messageToReplace = lastMessage;
              logger.info("🔄 Backend flagged regeneration - replacing last assistant message", {
                lastMessageId: String(lastMessage?.id),
                newMessageId: messageId,
              });
            }
            // If last message is user, try the previous assistant message
            else if (lastMessage && lastMessage.role === "user") {
              const prevMessage = chat.messages[chat.messages.length - 2];

              if (prevMessage && prevMessage.role === "assistant") {
                isRegenerationContext = true;
                messageToReplace = prevMessage;
                logger.info(
                  "🔄 Backend flagged regeneration - replacing previous assistant message",
                  {
                    lastMessageId: String(lastMessage?.id),
                    targetMessageId: String(messageToReplace.id),
                  },
                );
              }
            }
          }

          if (isRegenerationContext && messageToReplace) {
            // Find the message to replace (last assistant message or previous assistant message)
            if (!messageToReplace) {
              logger.warn(`⚠️ Could not find message to replace for regeneration`);
            } else {
              logger.info(
                `🔄 Regeneration detected, replacing message ${messageToReplace.id} with ${messageId}`,
              );

              const messageIdx = chat.messages.findIndex(
                (m: ChatMessage) => m.id === messageToReplace!.id,
              );

              if (messageIdx !== -1) {
                const updatedMessages = [...chat.messages];

                updatedMessages[messageIdx] = {
                  ...messageToReplace,
                  id: messageId, // Update to new message ID
                  content: data || "", // Use the incoming data
                  status: "streaming",
                  timestamp: new Date().toISOString(),
                  attempts: messageToReplace.attempts || 1,
                  currentAttempt: messageToReplace.currentAttempt || 1,
                };

                logger.info(
                  `🔄 Replaced message with regenerated content, starting progressive display`,
                );

                // Only update state if SSE connection is still active
                if (signal.aborted) {
                  logger.info("🛑 SSE connection aborted, skipping regeneration update");

                  return;
                }

                logger.info(`📊 Updated message`, {
                  id: messageId,
                  attempts: String(messageToReplace.attempts || 1),
                  currentAttempt: String(messageToReplace.currentAttempt || 1),
                  contentLength: String(data?.length || 0),
                });

                // Let React batch updates naturally (React 18 automatic batching)
                set({ chat: { ...chat, messages: updatedMessages } });

                return;
              }
            }
          } else {
            // Last message is user → new conversation → create new message
            logger.info(
              `💬 New conversation context detected (last message is user), creating new assistant message ${messageId}`,
            );
          }

          // Fallback: Create new streaming message (normal flow)
          const newMessage: ExtendedChatMessage = {
            id: messageId,
            role: messageType === "user" ? "user" : "assistant",
            agent,
            content: data,
            status: "streaming",
            liked: null,
            timestamp: new Date().toISOString(),
            messageType,
          };

          logger.info(`🆕 Creating new streaming message ${messageId}`);

          // Only update state if SSE connection is still active
          if (!signal.aborted) {
            // Let React batch updates naturally (React 18 automatic batching)
            set({ chat: { ...chat, messages: [...chat.messages, newMessage] } });
          }
        }

        // Performance telemetry - end measurement
        performance.measure("sse-message-process", {
          start: messageProcessStart,
          end: performance.now(),
        });
      };

      const finalizeMessage = (
        event: MessageEvent,
        status: "success" | "failed" | "stopped" = "success",
      ) => {
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring finalize event");

          return;
        }

        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (error) {
          logger.error("Failed to parse SSE finalize message", error);

          return;
        }

        // For message_completed events, the actual message data is in the 'data' field
        let messageData = msg;

        if (isMessageCompletedEvent(msg) && msg.data) {
          messageData = msg.data;
        }

        const data = messageData.data || "";
        const agent = messageData.agent;
        const messageId = extractMessageId(messageData);
        const messageType = extractMessageType(msg);

        // Extract citations if present (structured citations feature)
        const citations = msg.citations || messageData.citations || undefined;

        if (!messageId) {
          throw new Error("Message ID is required");
        }

        const chat = get().chat;

        if (!chat || signal.aborted) return;

        logger.info(`🏁 Finalizing message ${messageId}`);

        const messageIndex = chat.messages.findIndex((m: ChatMessage) => m.id === messageId);

        if (messageIndex === -1) {
          // Create new completed message (normal flow)
          logger.info(`🆕 Creating new completed message with backend ID ${messageId}`);
          const newMessage: ExtendedChatMessage = {
            id: messageId,
            role: messageType === "user" ? "user" : "assistant",
            agent,
            content: data,
            status: status,
            liked: null,
            timestamp: new Date().toISOString(),
            toolCallExecutions: messageData?.tool_call_executions || EMPTY_TOOL_EXECUTIONS,
            toolCalls: messageData?.tool_calls || EMPTY_TOOL_CALLS,
            usage: {
              promptTokens:
                messageData?.usage?.prompt_tokens || messageData?.usage?.promptTokens || 0,
              completionTokens:
                messageData?.usage?.completion_tokens || messageData?.usage?.completionTokens || 0,
              totalTokens: messageData?.usage?.total_tokens || messageData?.usage?.totalTokens || 0,
            },
            messageType,
            ...(citations && { citations }), // Add citations if present
          };

          // Only update state if SSE connection is still active
          if (!signal.aborted) {
            set({ chat: { ...chat, messages: [...chat.messages, newMessage] } });
          }

          // Detect artefacts in the new completed message content
          if (!signal.aborted && status === "success" && data) {
            try {
              const detectionResult = detectArtefacts(String(data), messageId);

              detectionResult.artefacts.forEach((artefact) => {
                useArtefactStore.getState().addArtefact(artefact, { source: "sse-streaming" });
              });

              // Register message-to-artefact mapping for navigation
              if (detectionResult.artefacts.length > 0) {
                const artefactIds = detectionResult.artefacts.map((a) => a.id);

                useArtefactStore.getState().registerMessageArtefacts(messageId, artefactIds);
              }

              if (detectionResult.warnings.length > 0) {
                logger.warn("Artefact detection warnings", {
                  warnings: String(detectionResult.warnings),
                });
              }

              logger.info(
                `🎨 Detected ${detectionResult.artefacts.length} artefacts in new message ${messageId}`,
                {
                  artefactCount: String(detectionResult.artefacts.length),
                  skipped: String(detectionResult.skippedCount),
                },
              );
            } catch (error) {
              logger.error("Failed to detect artefacts", error);
            }
          }

          // Store citations if present
          if (!signal.aborted && citations && citations.length > 0) {
            try {
              useArtefactStore.getState().addCitations(messageId, citations);
              logger.info(`📝 Stored ${citations.length} citations for message ${messageId}`);
            } catch (error) {
              logger.error("Failed to store citations", error);
            }
          }
        } else {
          // Update existing streaming message to completed
          logger.info(
            `✅ Finalization: Found existing message ${messageId} at index ${messageIndex}`,
          );
          const message = chat.messages[messageIndex];

          if (message.status !== "streaming") {
            logger.info(
              `⚠️ Message ${messageId} is not streaming (status: ${message.status}), skipping finalization`,
            );

            return;
          }

          const updatedMessages = [...chat.messages];

          // When finalizing, preserve existing content if new data is empty
          const finalContent = data || message.content;

          updatedMessages[messageIndex] = {
            ...message,
            content: finalContent,
            status: status,
            toolCallExecutions:
              messageData?.tool_call_executions ||
              message.toolCallExecutions ||
              EMPTY_TOOL_EXECUTIONS,
            toolCalls: messageData?.tool_calls || message.toolCalls || EMPTY_TOOL_CALLS,
            usage: {
              promptTokens:
                messageData?.usage?.prompt_tokens || messageData?.usage?.promptTokens || 0,
              completionTokens:
                messageData?.usage?.completion_tokens || messageData?.usage?.completionTokens || 0,
              totalTokens: messageData?.usage?.total_tokens || messageData?.usage?.totalTokens || 0,
            },
            // Preserve messageType from SSE or existing message
            messageType: messageType || (message as ExtendedChatMessage).messageType,
            ...(citations && { citations }), // Add citations if present
          };

          // Only update state if SSE connection is still active
          if (!signal.aborted) {
            set({ chat: { ...chat, messages: updatedMessages } });
          }

          // Detect artefacts in the completed message content
          if (!signal.aborted && status === "success" && finalContent) {
            try {
              const detectionResult = detectArtefacts(String(finalContent), messageId);

              detectionResult.artefacts.forEach((artefact) => {
                useArtefactStore.getState().addArtefact(artefact, { source: "sse-streaming" });
              });

              // Register message-to-artefact mapping for navigation
              if (detectionResult.artefacts.length > 0) {
                const artefactIds = detectionResult.artefacts.map((a) => a.id);

                useArtefactStore.getState().registerMessageArtefacts(messageId, artefactIds);
              }

              if (detectionResult.warnings.length > 0) {
                logger.warn("Artefact detection warnings", {
                  warnings: String(detectionResult.warnings),
                });
              }

              logger.info(
                `🎨 Detected ${detectionResult.artefacts.length} artefacts in message ${messageId}`,
                {
                  artefactCount: String(detectionResult.artefacts.length),
                  skipped: String(detectionResult.skippedCount),
                },
              );
            } catch (error) {
              logger.error("Failed to detect artefacts", error);
            }
          }

          // Store citations if present
          if (!signal.aborted && citations && citations.length > 0) {
            try {
              useArtefactStore.getState().addCitations(messageId, citations);
              logger.info(
                `📝 Stored ${citations.length} citations for updated message ${messageId}`,
              );
            } catch (error) {
              logger.error("Failed to store citations", error);
            }
          }
        }

        // Only proceed with save operations if SSE connection is still active
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, skipping save operations");

          return;
        }

        const updatedChat = get().chat;

        if (!updatedChat) {
          throw new Error("Chat is required");
        }

        // Save the chat (avoid infinite loops by doing this asynchronously)
        setTimeout(async () => {
          if (!signal.aborted && get().chat?.id === updatedChat.id) {
            try {
              await get().save(adminClient, updatedChat);
            } catch (error) {
              logger.error("❌ Background save failed", error);
            }
          }
        }, 0);

        // Trigger auto-save mechanism for successful messages (only if connection is still active)
        if (
          !signal.aborted &&
          typeof fetchChatHistory === "function" &&
          typeof userId === "string" &&
          status === "success"
        ) {
          const assistantSuccessMessages = updatedChat.messages.filter(
            (m: any) => m.role === "assistant" && m.status === "success",
          );

          if (assistantSuccessMessages.length === 1) {
            logger.info("🔄 Triggering fetchChatHistory after message completion");
            // Wrap in setTimeout to avoid state updates during render
            setTimeout(() => {
              if (!signal.aborted) {
                try {
                  fetchChatHistory(userId, adminClient);
                } catch (error) {
                  // Silently handle errors from fetchChatHistory to prevent unmounted component updates
                  logger.warn("⚠️ fetchChatHistory failed (component may be unmounted)", {
                    error: String(error),
                  });
                }
              }
            }, 100);
          }
        }
      };

      const handleWorkflowCompletion = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring workflow completion");

          return;
        }

        // Parse the event to check for stopped/cancelled status and messages_discarded flag
        let eventData;

        try {
          eventData = JSON.parse(event.data);
        } catch (e) {
          logger.warn("Failed to parse workflow completion event", { error: String(e) });
          eventData = {};
        }

        const status = eventData.status;

        // If the workflow was stopped by user, mark the message as "stopped" but keep it visible
        // Messages will be removed when user sends a new message (handled in invoke function)
        if (status === "stopped" || status === "cancelled") {
          // Finalize with "stopped" status to show partial response with stopped indicator
          finalizeMessage(event, "stopped");
          set({ streaming: false });

          // Close connection
          cleanup();

          return;
        }

        set({ streaming: false });
        finalizeMessage(event, "success");

        // Close connection immediately to prevent eventsource polyfill auto-reconnect errors
        // Will be re-opened if user triggers regeneration
        logger.info("🔌 Closing SSE connection after workflow completion");
        cleanup();
      };

      const handleMessageCompletion = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring message completion");

          return;
        }

        // Parse event data to check for regeneration flag
        let parsedData;

        try {
          parsedData = JSON.parse(event.data);
        } catch (e) {
          logger.error("Failed to parse message completion event data", e);
          parsedData = {};
        }

        const messageId = extractMessageId(parsedData);
        const isRegenerationComplete =
          parsedData.is_regeneration || parsedData.data?.is_regeneration;

        if (isRegenerationComplete && messageId) {
          logger.info(`🔄 Regeneration completion for message ${messageId}`);

          // DON'T update message status to success yet - let SSE streaming complete first
          // The message should remain in "streaming" status until handleFinalize is called
          logger.info(
            `🔄 Regeneration content received for message ${messageId}, keeping streaming status for SSE`,
          );

          const chat = get().chat;

          if (chat) {
            // Keep the message in streaming status, just update metadata like attempts
            const updatedMessages = chat.messages.map((msg) => {
              if (msg.id === messageId) {
                // Keep status as "streaming" to allow stop button to work during SSE streaming
                return { ...msg, status: "streaming" as const };
              }

              return msg;
            });

            set({ chat: { ...chat, messages: updatedMessages } });
          }
        }

        // Set streaming to false to allow user to send input or next message
        // But keep SSE connection open for multi-agent workflows
        set({ streaming: false });

        logger.info(
          `✅ Message ${messageId} completed, streaming set to false but SSE connection remains open for workflow continuation`,
        );

        finalizeMessage(event);

        // For simple chat_agent workflow, close the connection after message completes
        // For complex multi-agent workflows, keep connection open until workflow_completed
        const { chatWorkflowId } = get();

        // Parse event to check if this is a routing/system message
        // If so, keep connection open - more messages are expected
        let isRoutingMessage = false;

        try {
          const eventData = JSON.parse(event.data);
          const msgType = eventData.message_type || eventData.data?.message_type;
          const msgId = eventData.message_id || eventData.data?.message_id || "";

          isRoutingMessage = msgType === "system" || msgId.startsWith("routing_");
        } catch {
          // Ignore parse errors
        }

        if (isRoutingMessage) {
          logger.info(
            "🔗 Keeping SSE connection open after routing message - main response expected",
          );
        } else if (chatWorkflowId === "chat_agent") {
          logger.info("🔌 Closing SSE connection after message completion (chat_agent workflow)");
          cleanup();
        } else {
          logger.info("🔗 Keeping SSE connection open for multi-agent workflow continuation");
        }
      };

      const handleError = (event: MessageEvent) => {
        trackEvent(event);
        // Errors are deduplicated to prevent duplicate error messages and
        // double cleanup() calls when error events are replayed after reconnect
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring error event");

          return;
        }

        if (!event.data) return;
        const chat = get().chat;

        if (!chat || signal.aborted) return;

        const msg: ChatMessage = {
          id: String(Date.now()),
          role: "assistant",
          content: event.data || "An error occurred during the conversation",
          status: "failed",
          liked: null,
          timestamp: new Date().toISOString(),
        };

        // Only update state if SSE connection is still active
        if (!signal.aborted) {
          // Stop streaming indicator when error occurs
          set({ chat: { ...chat, messages: [...chat.messages, msg] }, streaming: false });
          // Defer save to avoid state updates during error handling
          setTimeout(() => {
            if (!signal.aborted) {
              get().save(adminClient, { ...chat, messages: [...chat.messages, msg] });
            }
          }, 0);
        }
        cleanup();
      };

      const handleRegenerationStarted = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection aborted, ignoring regeneration_started event");

          return;
        }

        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (error) {
          logger.error("Failed to parse regeneration_started event", error);

          return;
        }

        const data = msg.data || msg;
        const messageId = extractMessageId(data);
        const strategy = data.strategy;
        const attempt = data.attempt;
        const timestamp = data.timestamp;

        logger.info(`🔄 Regeneration started for message ${messageId}`, {
          strategy: String(strategy),
          attempt: String(attempt),
          timestamp: String(timestamp),
        });

        const chat = get().chat;

        if (!chat) return;

        // Find and update the message to show regeneration has started
        const messageIndex = chat.messages.findIndex((m: ChatMessage) => m.id === messageId);

        if (messageIndex !== -1) {
          const updatedMessages = [...chat.messages];

          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            status: "streaming",
            content: "", // Clear content to prepare for new regenerated content
          };

          logger.info(`🔄 Message ${messageId} marked as streaming (regeneration started)`);

          // Let React batch updates naturally (React 18 automatic batching)
          set({ chat: { ...chat, messages: updatedMessages } });
        }
      };

      const handleInputRequest = (event: MessageEvent) => {
        trackEvent(event);
        if (processEventIdAndCheckDuplicate(event)) return;
        if (signal.aborted) {
          logger.info("🛑 SSE connection inactive, ignoring input request");

          return;
        }

        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (error) {
          logger.error("Failed to parse input request", error);

          return;
        }

        const messageId = msg.message_id;
        const question = msg.data;
        const agent = msg.agent;

        logger.info("🎤 Input request received", {
          messageId,
          question: question?.substring(0, 100) || "[empty]",
          agent: String(agent),
        });

        // Store the input request state so the next user message is sent as input
        // The input request itself is NOT a message - it's just a signal that input is needed
        // The actual question/prompt should come through normal output events if it needs to be displayed
        set({
          pendingInputRequest: {
            messageId,
            question,
            agent,
            timestamp: new Date().toISOString(),
          },
        });

        logger.info("✅ Input request stored - waiting for user response");
        logger.info(
          "💡 If question needs to be displayed, it should come via normal output events",
        );
      };

      // Register event handlers on the initial EventSource
      registerEventListeners(currentSe);

      // Return cleanup function for external cleanup if needed
      return cleanup;
    },

    submitFeedback: async (adminClient, runId, messageId, liked, feedback) => {
      if (!adminClient) return;
      await adminClient?.chatHistories.setFeedback({
        feedback: feedback,
        runId: runId,
        messageId: messageId,
        liked: liked,
      });
    },

    load: async (adminClient, runId) => {
      logger.info("🔍 ChatStore: Loading chat with runId", {
        runId: String(runId),
        runIdType: typeof runId,
        runIdLength: String(runId?.length),
        containsSpaces: String(runId?.includes(" ")),
      });

      if (!adminClient || !runId) return;
      if (runId === get().runId) {
        return;
      }

      // Set loading state immediately
      set({ chatLoading: true });

      // Clean up any active SSE connections from previous chat before loading new one
      const currentRunId = get().runId;

      if (currentRunId) {
        const activeConnections = (window as any).__activeSSEConnections || {};
        const connection = activeConnections[currentRunId];

        if (connection) {
          logger.info("🛑 Cleaning up previous SSE connection when loading new chat", {
            runId: String(currentRunId),
          });

          // Abort the connection using AbortController
          if (connection.abortController) {
            connection.abortController.abort();
          }

          // Call cleanup function
          if (typeof connection.cleanup === "function") {
            connection.cleanup();
          }

          // Remove from registry
          delete activeConnections[currentRunId];
        }
      }

      logger.info("💭 Loading chat history", { runId: String(runId) });

      // Clear any existing artefacts and citations from previous chat
      useArtefactStore.getState().clearArtefacts();
      useArtefactStore.getState().clearCitations();

      // Force hide panel when loading from history (override localStorage state)
      const artefactStore = useArtefactStore.getState();

      if (artefactStore.artefactsVisible) {
        artefactStore.toggleArtefacts();
      }

      try {
        // Try to load from localStorage first as a fallback
        const chatStorageKey = `chat_${runId}`;
        let chatHistory = null;

        try {
          // Get chat history from the API with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

          chatHistory = await Promise.race([
            adminClient.chatHistories.get({ id: runId }),
            new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error("Request timeout after 10 seconds")), 10000);
            }),
          ]);

          clearTimeout(timeoutId);
          logger.info("✅ Chat history loaded from API", { chatId: String(chatHistory?.id) });
        } catch (apiError) {
          logger.warn("⚠️ Failed to load from API, trying localStorage", {
            error: String(apiError),
          });

          // Fallback to localStorage
          const stored = localStorage.getItem(chatStorageKey);

          if (stored) {
            try {
              chatHistory = JSON.parse(stored);
              logger.info("✅ Chat history loaded from localStorage", {
                chatId: String(chatHistory?.id),
              });
            } catch (parseError) {
              logger.error("❌ Failed to parse stored chat", parseError);
              throw new Error("Both API and localStorage failed");
            }
          } else {
            throw apiError; // Re-throw original API error if no localStorage backup
          }
        }

        // Ensure messages have the correct structure
        if (chatHistory && chatHistory.messages) {
          const messagesWithTools = chatHistory.messages.map((message: ChatMessage) => {
            const msgAny = message as any;
            const toolCallExecutions =
              message.toolCallExecutions ||
              msgAny.tool_call_executions ||
              msgAny.toolcallexecutions ||
              EMPTY_TOOL_EXECUTIONS;
            const toolCalls =
              message.toolCalls || msgAny.tool_calls || msgAny.toolcalls || EMPTY_TOOL_CALLS;

            // Normalize files from various sources
            const topLevelFiles = Array.isArray((msgAny as any).files) ? (msgAny as any).files : [];
            const attachments = Array.isArray((msgAny as any).attachments)
              ? (msgAny as any).attachments
              : [];
            const contentFiles =
              msgAny && msgAny.content && Array.isArray((msgAny as any).content?.files)
                ? (msgAny as any).content.files
                : [];
            const rawFiles =
              topLevelFiles.length > 0
                ? topLevelFiles
                : attachments.length > 0
                  ? attachments
                  : contentFiles;

            // Normalize file fields
            const normalizedFiles = Array.isArray(rawFiles)
              ? rawFiles
                  .map((f: any) => ({
                    id: f.id ?? f.file_id ?? f.uuid ?? String(f.name || f.filename || ""),
                    name: f.name ?? f.filename ?? "",
                    type: f.type ?? f.mime_type ?? f.file_type ?? "application/octet-stream",
                    size: String(f.size ?? f.filesize ?? ""),
                  }))
                  .filter((f: any) => f.id || f.name)
              : [];

            // Handle content with files
            let contentVal: any = msgAny.content;

            if (normalizedFiles.length > 0) {
              if (typeof contentVal === "string") {
                contentVal = { prompt: contentVal, files: normalizedFiles };
              } else if (contentVal && typeof contentVal === "object") {
                const embedded = Array.isArray((contentVal as any).files)
                  ? (contentVal as any).files
                  : undefined;

                contentVal = embedded ? contentVal : { ...contentVal, files: normalizedFiles };
              }
            }

            // Extract attempt data from various sources
            // Check both camelCase and snake_case fields
            // Note: TypeScript client uses camelCase, but API might return snake_case
            const attemptHistory =
              msgAny.attempt_history ||
              msgAny.attemptHistory ||
              (message as any).attempt_history ||
              message.attemptHistory ||
              [];

            // Log raw message data for debugging
            if (message.role === "assistant") {
              logger.info("🔍 Raw message data for attempt extraction", {
                messageId: String(message.id),
                hasAttemptHistory: String(!!msgAny.attempt_history),
                hasAttemptHistoryCamel: String(!!msgAny.attemptHistory),
                attemptHistoryLength: String(attemptHistory.length),
              });
            }

            const attempts =
              message.attempts ?? (attemptHistory.length > 0 ? attemptHistory.length : 1);
            const currentAttempt = message.currentAttempt || msgAny.currentAttempt || 1;

            // Check if main content is a system message and extract from attempt_history if needed
            let resolvedContent = contentVal;

            if (
              typeof contentVal === "string" &&
              (contentVal.includes("Direct call completed") ||
                contentVal.includes("response via agent reply mechanism")) &&
              attemptHistory.length > 0
            ) {
              // Find content for current attempt
              const currentAttemptData = attemptHistory.find(
                (attempt: any) => attempt.attempt_number === currentAttempt,
              );

              if (currentAttemptData && currentAttemptData.content) {
                resolvedContent = currentAttemptData.content;
                logger.info(
                  `🔄 ChatStore: Resolved system message to actual content for attempt ${currentAttempt}`,
                  {
                    messageId: String(message.id),
                    systemMessage: contentVal.substring(0, 50) + "...",
                    resolvedContentPreview: resolvedContent.substring(0, 100) + "...",
                    strategy: String(currentAttemptData.variation_strategy),
                  },
                );
              }
            }

            // Extract messageType from various sources (snake_case from API, camelCase from localStorage)
            const messageType: MessageType =
              msgAny.message_type || msgAny.messageType || "assistant";

            // Debug logging for attempt data extraction
            logger.info("🔍 ChatStore: Processing message attempt data", {
              messageId: String(message.id),
              messageRole: String(message.role),
              messageType: String(messageType),
              finalAttempts: String(attempts),
              finalCurrentAttempt: String(currentAttempt),
              attemptHistoryCount: String(attemptHistory.length),
              shouldShow: String(attempts > 1),
            });

            return {
              ...message,
              content: resolvedContent, // Use resolved content instead of original contentVal
              toolCallExecutions,
              toolCalls,
              messageType, // Preserve messageType for UI rendering
              ...(normalizedFiles.length > 0 ? { files: normalizedFiles } : {}),
              ...(attempts ? { attempts } : {}),
              ...(currentAttempt ? { currentAttempt } : {}),
              ...(attemptHistory.length > 0
                ? { attempt_history: attemptHistory, attemptHistory }
                : {}),
            };
          });

          set({
            chat: {
              ...chatHistory,
              messages: messagesWithTools,
            },
            streaming: false,
            chatLoading: false,
            runId: chatHistory.id,
            chatWorkflowId: chatHistory.workflowType || "chat_agent",
          });

          // Process loaded messages for artefact detection and citations
          logger.info("🔍 Processing loaded messages for artefacts and citations...");
          let totalArtefactsFound = 0;
          let totalCitationsFound = 0;

          messagesWithTools.forEach((message: ChatMessage) => {
            // Only process assistant messages that have content
            if (
              message.role === "assistant" &&
              message.content &&
              typeof message.content === "string"
            ) {
              try {
                const detectionResult = detectArtefacts(message.content, message.id);

                detectionResult.artefacts.forEach((artefact) => {
                  useArtefactStore.getState().addArtefact(artefact, { source: "history-load" });
                  totalArtefactsFound++;
                });

                // Register message-to-artefact mapping for navigation
                if (detectionResult.artefacts.length > 0) {
                  const artefactIds = detectionResult.artefacts.map((a) => a.id);

                  useArtefactStore.getState().registerMessageArtefacts(message.id, artefactIds);
                }

                if (detectionResult.warnings.length > 0) {
                  logger.warn(`Artefact detection warnings for message ${message.id}`, {
                    warnings: String(detectionResult.warnings),
                  });
                }
              } catch (error) {
                logger.warn(`Failed to detect artefacts for message ${message.id}`, {
                  error: String(error),
                });
              }
            }

            // Process citations from loaded messages
            const messageWithCitations = message as any;

            if (messageWithCitations.citations && Array.isArray(messageWithCitations.citations)) {
              try {
                useArtefactStore
                  .getState()
                  .addCitations(message.id, messageWithCitations.citations);
                totalCitationsFound += messageWithCitations.citations.length;
                logger.info(
                  `📝 Found ${messageWithCitations.citations.length} citations in loaded message ${message.id}`,
                );
              } catch (error) {
                logger.warn(`Failed to process citations for message ${message.id}`, {
                  error: String(error),
                });
              }
            }
          });

          if (totalArtefactsFound > 0) {
            logger.info(`✅ Found ${totalArtefactsFound} artefacts in loaded chat history`);
          }
          if (totalCitationsFound > 0) {
            logger.info(`✅ Found ${totalCitationsFound} citations in loaded chat history`);
          }

          logger.info(`✅ Chat loaded successfully with ${messagesWithTools.length} messages`);
        } else {
          set({ chat: chatHistory, streaming: false, chatLoading: false, runId: chatHistory.id });

          // Process any messages that might exist in the chatHistory for artefacts and citations
          if (chatHistory && chatHistory.messages && Array.isArray(chatHistory.messages)) {
            logger.info("🔍 Processing loaded messages for artefacts and citations...");
            let totalArtefactsFound = 0;
            let totalCitationsFound = 0;

            chatHistory.messages.forEach((message: ChatMessage) => {
              // Only process assistant messages that have content
              if (
                message.role === "assistant" &&
                message.content &&
                typeof message.content === "string"
              ) {
                try {
                  const detectionResult = detectArtefacts(message.content, message.id);

                  detectionResult.artefacts.forEach((artefact) => {
                    useArtefactStore.getState().addArtefact(artefact, { source: "history-load" });
                    totalArtefactsFound++;
                  });

                  // Register message-to-artefact mapping for navigation
                  if (detectionResult.artefacts.length > 0) {
                    const artefactIds = detectionResult.artefacts.map((a) => a.id);

                    useArtefactStore.getState().registerMessageArtefacts(message.id, artefactIds);
                  }

                  if (detectionResult.warnings.length > 0) {
                    logger.warn(`Artefact detection warnings for message ${message.id}`, {
                      warnings: String(detectionResult.warnings),
                    });
                  }
                } catch (error) {
                  logger.warn(`Failed to detect artefacts for message ${message.id}`, {
                    error: String(error),
                  });
                }
              }

              // Process citations from loaded messages
              const messageWithCitations = message as any;

              if (messageWithCitations.citations && Array.isArray(messageWithCitations.citations)) {
                try {
                  useArtefactStore
                    .getState()
                    .addCitations(message.id, messageWithCitations.citations);
                  totalCitationsFound += messageWithCitations.citations.length;
                  logger.info(
                    `📝 Found ${messageWithCitations.citations.length} citations in loaded message ${message.id}`,
                  );
                } catch (error) {
                  logger.warn(`Failed to process citations for message ${message.id}`, {
                    error: String(error),
                  });
                }
              }
            });

            if (totalArtefactsFound > 0) {
              logger.info(`✅ Found ${totalArtefactsFound} artefacts in loaded chat history`);
            }
            if (totalCitationsFound > 0) {
              logger.info(`✅ Found ${totalCitationsFound} citations in loaded chat history`);
            }
          }

          logger.info("✅ Chat loaded successfully (no messages)");
        }
      } catch (error) {
        logger.error("❌ Error loading chat history", error);

        // Don't reset to null - this breaks the UI. Instead, create minimal chat structure
        const fallbackChat = {
          id: runId,
          messages: [],
          timestamp: new Date().toISOString(),
          tenant: "unknown",
          username: "unknown",
          modelUsed: "unknown",
          summary: "Failed to load chat history",
          metrics: { usage: {} },
        };

        set({
          chat: fallbackChat,
          streaming: false,
          chatLoading: false,
          runId: runId,
        });

        logger.info("⚠️ Created fallback chat structure for", { runId: String(runId) });
      }
    },

    updateMessageFeedback: (messageId: string, liked: boolean | null) => {
      const chat = get().chat;

      if (!chat) return;

      const updatedMessages = chat.messages.map((message: ChatMessage) =>
        message.id === messageId ? { ...message, liked } : message,
      );

      set({ chat: { ...chat, messages: updatedMessages } });
    },

    updateMessageComment: (messageId: string, feedback: string) => {
      const chat = get().chat;

      if (!chat) return;

      const updatedMessages = chat.messages.map((message: ChatMessage) =>
        message.id === messageId ? { ...message, feedback } : message,
      );

      set({ chat: { ...chat, messages: updatedMessages } });
    },

    updateMessage: (chatId: string, messageId: string, updates: Partial<ChatMessage>) => {
      const chat = get().chat;

      if (!chat || chat.id !== chatId) return;

      const updatedMessages = chat.messages.map((message: ChatMessage) =>
        message.id === messageId ? { ...message, ...updates } : message,
      );

      set({ chat: { ...chat, messages: updatedMessages } });
    },
  };
}); // Added missing closing for the return statement

export { useChatStore };
